import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Megaphone, Video, Trash2, DownloadCloud, Loader2 } from "lucide-react";
import AdminWorkouts from "@/components/AdminWorkouts";
import AdminArticles from "@/components/AdminArticles";
import AdminCoaching from "@/components/AdminCoaching";
import AdminUsers from "@/components/AdminUsers";
import AdminSupport from "@/components/AdminSupport";

const VALID_TABS = ["users", "workouts", "articles", "coaching", "support", "content", "announce"];

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(VALID_TABS.includes(initialTab) ? initialTab : "users");
  const [stats, setStats] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [ann, setAnn] = useState({ title: "", text: "" });
  const [override, setOverride] = useState({ id: "", url: "" });
  const [prefetch, setPrefetch] = useState(null);
  const [warming, setWarming] = useState(false);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => toast.error("Admin only"));
    api.get("/exercises").then(({ data }) => setExercises(data)).catch(() => {});
    api.get("/admin/prefetch-videos/status").then(({ data }) => setPrefetch(data)).catch(() => {});
  };
  useEffect(load, []);

  // While a prefetch batch is running, poll status so the progress bar advances live.
  useEffect(() => {
    if (!prefetch?.running) return undefined;
    const t = setInterval(() => {
      api.get("/admin/prefetch-videos/status").then(({ data }) => setPrefetch(data)).catch(() => {});
    }, 2500);
    return () => clearInterval(t);
  }, [prefetch?.running]);

  const warmCache = async () => {
    setWarming(true);
    try {
      const { data } = await api.post("/admin/prefetch-videos", { limit: 40 });
      setPrefetch(data);
      if (data.already_running) toast.info("A prefetch batch is already running");
      else toast.success("Warming video cache…");
    } catch (e) { toast.error("Could not start video prefetch"); }
    finally { setWarming(false); }
  };

  const postAnnouncement = async () => {
    if (!ann.title || !ann.text) return;
    await api.post("/admin/announcements", ann);
    setAnn({ title: "", text: "" }); toast.success("Announcement posted");
  };

  const saveOverride = async () => {
    if (!override.id || !override.url) return;
    await api.post(`/admin/exercises/${override.id}/override-video`, { youtube_url: override.url });
    toast.success("Video override saved"); setOverride({ id: "", url: "" });
  };

  return (
    <AppShell title="Admin" hideNav right={<Button size="sm" onClick={() => navigate("/dashboard")} className="bg-secondary border border-border text-foreground"><ChevronLeft className="h-4 w-4" /> App</Button>}>
      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[["Users", stats.users],["Active", stats.active_subscribers],["Trial", stats.trialing],["Workouts", stats.workouts],["Exercises", stats.exercises],["Paid", stats.paid_transactions]].map(([l, v]) => (
            <Card key={l} className="rounded-xl bg-card border-border p-2 text-center"><p className="font-mono text-xl font-bold">{v}</p><p className="text-[10px] text-muted-foreground">{l}</p></Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border w-full overflow-x-auto rd-scrollbar">
          <TabsTrigger value="users" data-testid="atab-users" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Users</TabsTrigger>
          <TabsTrigger value="workouts" data-testid="atab-workouts" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Workouts</TabsTrigger>
          <TabsTrigger value="articles" data-testid="atab-articles" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Articles</TabsTrigger>
          <TabsTrigger value="coaching" data-testid="atab-coaching" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Coaching</TabsTrigger>
          <TabsTrigger value="support" data-testid="atab-support" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Support</TabsTrigger>
          <TabsTrigger value="content" data-testid="atab-content" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Videos</TabsTrigger>
          <TabsTrigger value="announce" data-testid="atab-announce" className="flex-1 data-[state=active]:bg-[#ccff0c] data-[state=active]:text-black">Announce</TabsTrigger>
        </TabsList>

        <TabsContent value="workouts"><AdminWorkouts /></TabsContent>
        <TabsContent value="articles"><AdminArticles /></TabsContent>
        <TabsContent value="coaching"><AdminCoaching /></TabsContent>
        <TabsContent value="support"><AdminSupport /></TabsContent>

        <TabsContent value="users" className="mt-0"><AdminUsers /></TabsContent>

        <TabsContent value="content" className="mt-4 space-y-3">
          <Card className="rounded-2xl bg-card border-border p-4">
            <div className="flex items-center gap-2 mb-1"><DownloadCloud className="h-4 w-4 text-[#ccff0c]" /><p className="font-semibold text-sm">Exercise demo videos</p></div>
            <p className="text-xs text-muted-foreground mb-3">Pre-load YouTube demos so they open instantly for members. Runs in batches (YouTube allows roughly 100 lookups/day) — press again anytime to pick up where it left off.</p>
            {prefetch && (
              <>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-muted-foreground" data-testid="prefetch-cached-count">{prefetch.cached} / {prefetch.total} cached</span>
                  <span className="font-mono rd-lime">{prefetch.remaining} left</span>
                </div>
                <Progress data-testid="prefetch-progress" value={prefetch.total ? (prefetch.cached / prefetch.total) * 100 : 0} className="h-2 mb-3" />
                {prefetch.running && (
                  <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Warming… {prefetch.succeeded} added this run</p>
                )}
                {!prefetch.running && prefetch.quota_exceeded && (
                  <p className="text-xs mb-2" style={{ color: "hsl(var(--warning))" }}>Daily YouTube limit reached — resume tomorrow to cache the rest.</p>
                )}
              </>
            )}
            <Button data-testid="warm-video-cache" onClick={warmCache} disabled={warming || prefetch?.running} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
              {prefetch?.running ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Warming cache…</> : <><DownloadCloud className="h-4 w-4 mr-1" /> Warm video cache</>}
            </Button>
          </Card>

          <Card className="rounded-2xl bg-card border-border p-4">
            <div className="flex items-center gap-2 mb-3"><Video className="h-4 w-4 text-[#ccff0c]" /><p className="font-semibold text-sm">Override exercise video</p></div>
            <div className="space-y-2">
              <select data-testid="override-exercise" value={override.id} onChange={(e) => setOverride((o) => ({ ...o, id: e.target.value }))} className="w-full bg-card border border-border rounded-lg h-10 px-3 text-sm">
                <option value="">Select exercise…</option>
                {exercises.map((ex) => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
              </select>
              <Input data-testid="override-url" value={override.url} onChange={(e) => setOverride((o) => ({ ...o, url: e.target.value }))} placeholder="https://youtube.com/watch?v=..." className="bg-card border-border h-10" />
              <Button data-testid="save-override" onClick={saveOverride} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Save override</Button>
            </div>
          </Card>
          <p className="text-xs text-muted-foreground text-center">{exercises.length} exercises · {stats?.workouts || 0} workouts · {stats?.articles || 0} articles seeded.</p>
        </TabsContent>

        <TabsContent value="announce" className="mt-4">
          <Card className="rounded-2xl bg-card border-border p-4 space-y-3">
            <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#ccff0c]" /><p className="font-semibold text-sm">Post announcement</p></div>
            <div><Label className="text-xs">Title</Label><Input data-testid="ann-title" value={ann.title} onChange={(e) => setAnn((a) => ({ ...a, title: e.target.value }))} className="bg-card border-border h-10" /></div>
            <div><Label className="text-xs">Message</Label><Textarea data-testid="ann-text" value={ann.text} onChange={(e) => setAnn((a) => ({ ...a, text: e.target.value }))} className="bg-card border-border" /></div>
            <Button data-testid="post-announcement" onClick={postAnnouncement} className="w-full bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">Post to community</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
